import React, { useState, useEffect, useCallback } from "react";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { DownloadOutlined, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import api from "../services/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// yyyy-MM-dd -> dd-MM-yyyy
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = String(dateString).split("-");
  if (!year || !month || !day) return "";
  return `${day}-${month}-${year}`;
};

// dd-MM-yyyy -> yyyy-MM-dd
const convertToYYYYMMDD = (dateString) => {
  if (!dateString) return "";
  const [day, month, year] = String(dateString).split("-");
  if (!day || !month || !year) return "";
  return `${year}-${month}-${day}`;
};

// 1er jour du mois précédent par défaut
// const getFirstDayOfMonth = () => {
//   const currentDate = new Date();
//   currentDate.setUTCDate(1);
//   currentDate.setUTCMonth(currentDate.getUTCMonth() - 1);
//   return formatDateToDDMMYYYY(currentDate.toISOString().split("T")[0]);
// };
const getFirstDayOfMonth = () => {
  const now = new Date();
  const firstDay = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
  );
  return formatDateToDDMMYYYY(firstDay.toISOString().split("T")[0]);
};

// dernier jour du mois précédent par défaut
const getLastDayOfMonth = () => {
  const currentDate = new Date();
  const lastDay = new Date(
    Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 0)
  );
  return formatDateToDDMMYYYY(lastDay.toISOString().split("T")[0]);
};

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const parseDDMMYYYY = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = String(dateString).split("-").map(Number);
  if (!day || !month || !year) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

const formatUTCDateToDDMMYYYY = (date) => {
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

// Renvoie la période complète du mois de la date fournie
const getMonthPeriodFromDate = (dateString) => {
  const baseDate = parseDDMMYYYY(dateString);
  if (!baseDate) return { startDate: "", endDate: "" };

  const year = baseDate.getUTCFullYear();
  const month = baseDate.getUTCMonth();

  const monthStart = new Date(Date.UTC(year, month, 1));
  const monthEnd = new Date(Date.UTC(year, month + 1, 0));

  return {
    startDate: formatUTCDateToDDMMYYYY(monthStart),
    endDate: formatUTCDateToDDMMYYYY(monthEnd),
  };
};

const getPreviousMonthPeriod = (dateString) => {
  const baseDate = parseDDMMYYYY(dateString);
  if (!baseDate) return { startDate: "", endDate: "" };

  const year = baseDate.getUTCFullYear();
  const month = baseDate.getUTCMonth();

  const previousMonthStart = new Date(Date.UTC(year, month - 1, 1));
  const previousMonthEnd = new Date(Date.UTC(year, month, 0));

  return {
    startDate: formatUTCDateToDDMMYYYY(previousMonthStart),
    endDate: formatUTCDateToDDMMYYYY(previousMonthEnd),
  };
};

const SituationCaisse = () => {
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getLastDayOfMonth());

  const [rows, setRows] = useState([]);
  const [isFetchingSituationCaisse, setIsFetchingSituationCaisse] =
    useState(false);
  const [isDataReadySituationCaisse, setIsDataReadySituationCaisse] =
    useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState("");

  const handleMonthChange = (inputValue) => {
    const ddmmyyyy = formatDateToDDMMYYYY(inputValue);
    const period = getMonthPeriodFromDate(ddmmyyyy);

    setStartDate(period.startDate);
    setEndDate(period.endDate);
  };

  const fetchSituationCaisseData = useCallback(async () => {
    setIsFetchingSituationCaisse(true);

    try {
      const previousMonthPeriod = getPreviousMonthPeriod(startDate);

      const [consommationsRes, tresoRes, tresoPreviousRes, sortieRes] =
        await Promise.all([
          api.get("/api/consommations/tresoreriedebutmois", {
            params: { startDate, endDate },
          }),
          api.get("/api/tresorerieglobale", {
            params: { startDate, endDate },
          }),
          api.get("/api/tresorerieglobale", {
            params: {
              startDate: previousMonthPeriod.startDate,
              endDate: previousMonthPeriod.endDate,
            },
          }),
          api.get("/api/sortiecaisse", {
            params: { startDate, endDate },
          }),
        ]);

      // Décommenter au besoin pour debug
      console.log("📅 Période sélectionnée:", startDate, "→", endDate);
      console.log(
        "📆 Mois précédent utilisé:",
        previousMonthPeriod.startDate,
        "→",
        previousMonthPeriod.endDate
      );
      console.log("🧾 API consommations:", consommationsRes.data);
      console.log("🏦 API trésorerie période courante:", tresoRes.data);
      console.log("🏦 API trésorerie mois précédent:", tresoPreviousRes.data);
      console.log("💸 API sorties caisse:", sortieRes.data);

      // On garde la structure actuelle que tu utilises pour l'API consommations
      const consommationTotalPaid = toNumber(
        consommationsRes.data?.total?.paidAmount
      );

      const tresoRecords = Array.isArray(tresoRes.data?.records)
        ? tresoRes.data.records
        : [];

      const tresoPreviousRecords = Array.isArray(tresoPreviousRes.data?.records)
        ? tresoPreviousRes.data.records
        : [];

      const totalSortieCaisseFm = Array.isArray(sortieRes.data)
        ? sortieRes.data.reduce((sum, item) => sum + toNumber(item.total), 0)
        : 0;

      // IMPORTANT :
      // on somme les lignes au lieu de prendre seulement la première
      const totalSoldeCaisseFm = tresoRecords.reduce(
        (sum, item) => sum + toNumber(item.soldeCaissefm),
        0
      );

      const totalTreso = tresoRecords.reduce(
        (sum, item) => sum + toNumber(item.totalTresoDispo),
        0
      );

      const totalSoldeComptes = tresoRecords.reduce(
        (sum, item) => sum + toNumber(item.totalSoldeComptes),
        0
      );

      const totalSoldeCaisseFmMoisPrecedent = tresoPreviousRecords.reduce(
        (sum, item) => sum + toNumber(item.soldeCaissefm),
        0
      );

      // Logique métier voulue
      const totalCaisseFm =
        consommationTotalPaid + totalSoldeCaisseFmMoisPrecedent;

      // On garde le comportement que tu voulais :
      // à l'initialisation, solde effectif = solde courant
      const soldeEffCaisseFm = totalSoldeCaisseFm;
      const differenceAJustifier = totalSoldeCaisseFm - soldeEffCaisseFm;

      // Décommenter au besoin pour debug
      // console.log("💰 totalPaidAmount (mois courant):", consommationTotalPaid);
      // console.log(
      //   "🏦 soldeCaissefm (mois précédent):",
      //   totalSoldeCaisseFmMoisPrecedent
      // );
      // console.log("🧮 totalCaisseFm calculé:", totalCaisseFm);
      // console.log("📤 totalSortieCaisseFm:", totalSortieCaisseFm);
      // console.log("🏦 totalSoldeCaisseFm:", totalSoldeCaisseFm);
      // console.log("💵 totalTreso:", totalTreso);
      // console.log("🏛 totalSoldeComptes:", totalSoldeComptes);

      setRows([
        {
          id: 1,
          totalCaisseFm: totalCaisseFm.toFixed(2),
          totalSortieCaisseFm: totalSortieCaisseFm.toFixed(2),
          totalSoldeCaisseFm: totalSoldeCaisseFm.toFixed(2),
          soldeEffCaisseFm: soldeEffCaisseFm.toFixed(2),
          differenceAJustifier: differenceAJustifier.toFixed(2),
          totalTreso: totalTreso.toFixed(2),
          totalSoldeComptes: totalSoldeComptes.toFixed(2),
        },
      ]);

      setIsDataReadySituationCaisse(true);
    } catch (error) {
      console.error("Error fetching situation caisse data:", error);
      toast.error("Erreur lors de la récupération de la situation de caisse.");
      setRows([]);
      setIsDataReadySituationCaisse(false);
    } finally {
      setIsFetchingSituationCaisse(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSituationCaisseData();
  }, [fetchSituationCaisseData]);

  const handleSave = async () => {
    try {
      const response = await api.post("/api/situationcaisse", {
        startDate,
        endDate,
        rows,
      });

      if (response.status === 201) {
        toast.success("Situation caisse enregistrée avec succès");
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.error);
      } else {
        console.error("Error saving data:", error);
        toast.error("Erreur lors de l'enregistrement");
      }
    }
  };

  const handleUpdate = async (id, updatedSoldeEffCaisseFm) => {
    try {
      const response = await api.put(`/api/situationcaisse/${id}`, {
        soldeEffCaisseFm: updatedSoldeEffCaisseFm,
      });

      const updatedRow = response.data;

      setRows((prevRows) =>
        prevRows.map((row) =>
          String(row.id) === String(id)
            ? {
                ...row,
                ...updatedRow,
                totalCaisseFm: String(
                  updatedRow.totalCaisseFm ?? row.totalCaisseFm
                ),
                totalSortieCaisseFm: String(
                  updatedRow.totalSortieCaisseFm ?? row.totalSortieCaisseFm
                ),
                totalSoldeCaisseFm: String(
                  updatedRow.totalSoldeCaisseFm ?? row.totalSoldeCaisseFm
                ),
                soldeEffCaisseFm: String(
                  updatedRow.soldeEffCaisseFm ?? updatedSoldeEffCaisseFm
                ),
                differenceAJustifier: String(
                  updatedRow.differenceAJustifier ?? row.differenceAJustifier
                ),
                totalTreso: String(updatedRow.totalTreso ?? row.totalTreso),
                totalSoldeComptes: String(
                  updatedRow.totalSoldeComptes ?? row.totalSoldeComptes
                ),
              }
            : row
        )
      );

      toast.success("Situation caisse mise à jour avec succès");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Veuillez enregistrer la situation caisse d'abord.");
      } else {
        console.error("Error updating data:", error);
        toast.error("Erreur lors de la mise à jour");
      }
    }
  };

  const handlePrint = () => {
    if (!isDataReadySituationCaisse) {
      toast.error(
        "Les données ne sont pas prêtes. Veuillez sélectionner une plage de dates et réessayer."
      );
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>Situation de Caisse</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
          .header p { margin: 0; }
          h1 { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
    `);

    printWindow.document.write(`
      <div class="header">
        <div><p>COPIFOR DE DAROU NGARAF</p></div>
        <div><p>Période du ${startDate} au ${endDate}</p></div>
      </div>
    `);

    printWindow.document.write("<h1>Situation de Caisse</h1>");
    printWindow.document.write("<table>");
    printWindow.document.write(`
      <tr>
        <th>Total Caisse Fin du Mois</th>
        <th>Total Sortie Caisse</th>
        <th>Total Solde Caisse</th>
        <th>Solde Effectif Caisse</th>
        <th>Différence à Justifier</th>
        <th>Total Trésorerie</th>
      </tr>
    `);

    rows.forEach((row) => {
      printWindow.document.write(`
        <tr>
          <td>${row.totalCaisseFm}</td>
          <td>${row.totalSortieCaisseFm}</td>
          <td>${row.totalSoldeCaisseFm}</td>
          <td>${row.soldeEffCaisseFm}</td>
          <td>${row.differenceAJustifier}</td>
          <td>${row.totalTreso}</td>
        </tr>
      `);
    });

    printWindow.document.write("</table>");
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  };

  const handleEditClick = (row) => {
    setEditRow(row);
    setEditValue(row.soldeEffCaisseFm);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditRow(null);
    setEditValue("");
  };

  const handleDialogSave = () => {
    if (!editRow) return;
    handleUpdate(editRow.id, editValue);
    handleDialogClose();
  };

  const columnsSituationCaisse = [
    {
      field: "totalCaisseFm",
      headerName: "Total Caisse Fin du Mois",
      flex: 1,
    },
    {
      field: "totalSortieCaisseFm",
      headerName: "Total Sortie Caisse",
      flex: 1,
    },
    {
      field: "totalSoldeCaisseFm",
      headerName: "Total Solde Caisse",
      flex: 1,
    },
    {
      field: "soldeEffCaisseFm",
      headerName: "Solde Effectif Caisse",
      flex: 1,
    },
    {
      field: "differenceAJustifier",
      headerName: "Différence à Justifier",
      flex: 1,
    },
    {
      field: "totalTreso",
      headerName: "Total Trésorerie",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton onClick={() => handleEditClick(params.row)}>
          <Edit />
        </IconButton>
      ),
    },
  ];

  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Typography>Aucune donnée disponible</Typography>
    </GridOverlay>
  );

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" gap={2}>
          <TextField
            id="start-date"
            label="Période de début"
            type="date"
            value={convertToYYYYMMDD(startDate)}
            onChange={(e) => handleMonthChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="end-date"
            label="Période de fin"
            type="date"
            value={convertToYYYYMMDD(endDate)}
            onChange={(e) => handleMonthChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<DownloadOutlined />}
            disabled={isFetchingSituationCaisse}
          >
            Enregistrer
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePrint}
            startIcon={<DownloadOutlined />}
            style={{ marginLeft: 16 }}
            disabled={isFetchingSituationCaisse}
          >
            Imprimer
          </Button>
        </Box>
      </Box>

      <DataGrid
        rows={rows}
        columns={columnsSituationCaisse}
        autoHeight
        loading={isFetchingSituationCaisse}
        pageSizeOptions={[10, 25, 50, 100]}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Modifier Solde Effectif Caisse</DialogTitle>
        <DialogContent>
          <TextField
            label="Solde Effectif Caisse"
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Annuler</Button>
          <Button onClick={handleDialogSave}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SituationCaisse;



// import React, { useState, useEffect, useCallback } from "react";
// import { DataGrid, GridOverlay } from "@mui/x-data-grid";
// import { DownloadOutlined, Edit } from "@mui/icons-material";
// import {
//   Box,
//   Button,
//   TextField,
//   Typography,
//   IconButton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
// } from "@mui/material";
// import api from "../services/api";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// // yyyy-MM-dd -> dd-MM-yyyy
// const formatDateToDDMMYYYY = (dateString) => {
//   if (!dateString) return "";
//   const [year, month, day] = String(dateString).split("-");
//   if (!year || !month || !day) return "";
//   return `${day}-${month}-${year}`;
// };

// // dd-MM-yyyy -> yyyy-MM-dd
// const convertToYYYYMMDD = (dateString) => {
//   if (!dateString) return "";
//   const [day, month, year] = String(dateString).split("-");
//   if (!day || !month || !year) return "";
//   return `${year}-${month}-${day}`;
// };

// // 1er jour du mois précédent par défaut
// // const getFirstDayOfMonth = () => {
// //   const currentDate = new Date();
// //   currentDate.setUTCDate(1);
// //   currentDate.setUTCMonth(currentDate.getUTCMonth() - 1);
// //   return formatDateToDDMMYYYY(currentDate.toISOString().split("T")[0]);
// // };
// const getFirstDayOfMonth = () => {
//   const now = new Date();
//   const firstDay = new Date(
//     Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
//   );
//   return formatDateToDDMMYYYY(firstDay.toISOString().split("T")[0]);
// };

// // dernier jour du mois précédent par défaut
// const getLastDayOfMonth = () => {
//   const currentDate = new Date();
//   const lastDay = new Date(
//     Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 0)
//   );
//   return formatDateToDDMMYYYY(lastDay.toISOString().split("T")[0]);
// };

// const toNumber = (value) => {
//   const n = parseFloat(value);
//   return Number.isFinite(n) ? n : 0;
// };

// const parseDDMMYYYY = (dateString) => {
//   if (!dateString) return null;
//   const [day, month, year] = String(dateString).split("-").map(Number);
//   if (!day || !month || !year) return null;
//   return new Date(Date.UTC(year, month - 1, day));
// };

// const formatUTCDateToDDMMYYYY = (date) => {
//   const day = String(date.getUTCDate()).padStart(2, "0");
//   const month = String(date.getUTCMonth() + 1).padStart(2, "0");
//   const year = date.getUTCFullYear();
//   return `${day}-${month}-${year}`;
// };

// const getPreviousMonthPeriod = (dateString) => {
//   const baseDate = parseDDMMYYYY(dateString);
//   if (!baseDate) return { startDate: "", endDate: "" };

//   const year = baseDate.getUTCFullYear();
//   const month = baseDate.getUTCMonth();

//   const previousMonthStart = new Date(Date.UTC(year, month - 1, 1));
//   const previousMonthEnd = new Date(Date.UTC(year, month, 0));

//   return {
//     startDate: formatUTCDateToDDMMYYYY(previousMonthStart),
//     endDate: formatUTCDateToDDMMYYYY(previousMonthEnd), 
//   };
// };

// const SituationCaisse = () => {
//   const [startDate, setStartDate] = useState(getFirstDayOfMonth());
//   const [endDate, setEndDate] = useState(getLastDayOfMonth());

//   const [rows, setRows] = useState([]);
//   const [isFetchingSituationCaisse, setIsFetchingSituationCaisse] =
//     useState(false);
//   const [isDataReadySituationCaisse, setIsDataReadySituationCaisse] =
//     useState(false);

//   const [openDialog, setOpenDialog] = useState(false);
//   const [editRow, setEditRow] = useState(null);
//   const [editValue, setEditValue] = useState("");

//   const fetchSituationCaisseData = useCallback(async () => {
//     setIsFetchingSituationCaisse(true);

//     try {
//       const previousMonthPeriod = getPreviousMonthPeriod(startDate);

//       const [consommationsRes, tresoRes, tresoPreviousRes, sortieRes] =
//         await Promise.all([
//           api.get("/api/consommations/tresoreriedebutmois", {
//             params: { startDate, endDate },
//           }),
//           api.get("/api/tresorerieglobale", {
//             params: { startDate, endDate },
//           }),
//           api.get("/api/tresorerieglobale", {
//             params: {
//               startDate: previousMonthPeriod.startDate,
//               endDate: previousMonthPeriod.endDate,
//             },
//           }),
//           api.get("/api/sortiecaisse", {
//             params: { startDate, endDate },
//           }),
//         ]);

//       // Décommenter au besoin pour debug
//       console.log("📅 Période sélectionnée:", startDate, "→", endDate);
//       console.log(
//         "📆 Mois précédent utilisé:",
//         previousMonthPeriod.startDate,
//         "→",
//         previousMonthPeriod.endDate
//       );
//       console.log("🧾 API consommations:", consommationsRes.data);
//       console.log("🏦 API trésorerie période courante:", tresoRes.data);
//       console.log("🏦 API trésorerie mois précédent:", tresoPreviousRes.data);
//       console.log("💸 API sorties caisse:", sortieRes.data);

//       // On garde la structure actuelle que tu utilises pour l'API consommations
//       const consommationTotalPaid = toNumber(
//         consommationsRes.data?.total?.paidAmount
//       );

//       const tresoRecords = Array.isArray(tresoRes.data?.records)
//         ? tresoRes.data.records
//         : [];

//       const tresoPreviousRecords = Array.isArray(tresoPreviousRes.data?.records)
//         ? tresoPreviousRes.data.records
//         : [];

//       const totalSortieCaisseFm = Array.isArray(sortieRes.data)
//         ? sortieRes.data.reduce((sum, item) => sum + toNumber(item.total), 0)
//         : 0;

//       // IMPORTANT :
//       // on somme les lignes au lieu de prendre seulement la première
//       const totalSoldeCaisseFm = tresoRecords.reduce(
//         (sum, item) => sum + toNumber(item.soldeCaissefm),
//         0
//       );

//       const totalTreso = tresoRecords.reduce(
//         (sum, item) => sum + toNumber(item.totalTresoDispo),
//         0
//       );

//       const totalSoldeComptes = tresoRecords.reduce(
//         (sum, item) => sum + toNumber(item.totalSoldeComptes),
//         0
//       );

//       const totalSoldeCaisseFmMoisPrecedent = tresoPreviousRecords.reduce(
//         (sum, item) => sum + toNumber(item.soldeCaissefm),
//         0
//       );

//       // Logique métier voulue
//       const totalCaisseFm =
//         consommationTotalPaid + totalSoldeCaisseFmMoisPrecedent;

//       // On garde le comportement que tu voulais :
//       // à l'initialisation, solde effectif = solde courant
//       const soldeEffCaisseFm = totalSoldeCaisseFm;
//       const differenceAJustifier = totalSoldeCaisseFm - soldeEffCaisseFm;

//       // Décommenter au besoin pour debug
//       // console.log("💰 totalPaidAmount (mois courant):", consommationTotalPaid);
//       // console.log(
//       //   "🏦 soldeCaissefm (mois précédent):",
//       //   totalSoldeCaisseFmMoisPrecedent
//       // );
//       // console.log("🧮 totalCaisseFm calculé:", totalCaisseFm);
//       // console.log("📤 totalSortieCaisseFm:", totalSortieCaisseFm);
//       // console.log("🏦 totalSoldeCaisseFm:", totalSoldeCaisseFm);
//       // console.log("💵 totalTreso:", totalTreso);
//       // console.log("🏛 totalSoldeComptes:", totalSoldeComptes);

//       setRows([
//         {
//           id: 1,
//           totalCaisseFm: totalCaisseFm.toFixed(2),
//           totalSortieCaisseFm: totalSortieCaisseFm.toFixed(2),
//           totalSoldeCaisseFm: totalSoldeCaisseFm.toFixed(2),
//           soldeEffCaisseFm: soldeEffCaisseFm.toFixed(2),
//           differenceAJustifier: differenceAJustifier.toFixed(2),
//           totalTreso: totalTreso.toFixed(2),
//           totalSoldeComptes: totalSoldeComptes.toFixed(2),
//         },
//       ]);

//       setIsDataReadySituationCaisse(true);
//     } catch (error) {
//       console.error("Error fetching situation caisse data:", error);
//       toast.error("Erreur lors de la récupération de la situation de caisse.");
//       setRows([]);
//       setIsDataReadySituationCaisse(false);
//     } finally {
//       setIsFetchingSituationCaisse(false);
//     }
//   }, [startDate, endDate]);

//   useEffect(() => {
//     fetchSituationCaisseData();
//   }, [fetchSituationCaisseData]);

//   const handleSave = async () => {
//     try {
//       const response = await api.post("/api/situationcaisse", {
//         startDate,
//         endDate,
//         rows,
//       });

//       if (response.status === 201) {
//         toast.success("Situation caisse enregistrée avec succès");
//       } else {
//         toast.error("Erreur lors de l'enregistrement");
//       }
//     } catch (error) {
//       if (error.response?.status === 409) {
//         toast.error(error.response.data.error);
//       } else {
//         console.error("Error saving data:", error);
//         toast.error("Erreur lors de l'enregistrement");
//       }
//     }
//   };

//   const handleUpdate = async (id, updatedSoldeEffCaisseFm) => {
//     try {
//       const response = await api.put(`/api/situationcaisse/${id}`, {
//         soldeEffCaisseFm: updatedSoldeEffCaisseFm,
//       });

//       const updatedRow = response.data;

//       setRows((prevRows) =>
//         prevRows.map((row) =>
//           String(row.id) === String(id)
//             ? {
//                 ...row,
//                 ...updatedRow,
//                 totalCaisseFm: String(
//                   updatedRow.totalCaisseFm ?? row.totalCaisseFm
//                 ),
//                 totalSortieCaisseFm: String(
//                   updatedRow.totalSortieCaisseFm ?? row.totalSortieCaisseFm
//                 ),
//                 totalSoldeCaisseFm: String(
//                   updatedRow.totalSoldeCaisseFm ?? row.totalSoldeCaisseFm
//                 ),
//                 soldeEffCaisseFm: String(
//                   updatedRow.soldeEffCaisseFm ?? updatedSoldeEffCaisseFm
//                 ),
//                 differenceAJustifier: String(
//                   updatedRow.differenceAJustifier ?? row.differenceAJustifier
//                 ),
//                 totalTreso: String(updatedRow.totalTreso ?? row.totalTreso),
//                 totalSoldeComptes: String(
//                   updatedRow.totalSoldeComptes ?? row.totalSoldeComptes
//                 ),
//               }
//             : row
//         )
//       );

//       toast.success("Situation caisse mise à jour avec succès");
//     } catch (error) {
//       if (error.response?.status === 404) {
//         toast.error("Veuillez enregistrer la situation caisse d'abord.");
//       } else {
//         console.error("Error updating data:", error);
//         toast.error("Erreur lors de la mise à jour");
//       }
//     }
//   };

//   const handlePrint = () => {
//     if (!isDataReadySituationCaisse) {
//       toast.error(
//         "Les données ne sont pas prêtes. Veuillez sélectionner une plage de dates et réessayer."
//       );
//       return;
//     }

//     const printWindow = window.open("", "_blank");
//     if (!printWindow) return;

//     printWindow.document.write(`
//       <html>
//       <head>
//         <title>Situation de Caisse</title>
//         <style>
//           body { font-family: Arial, sans-serif; margin: 40px; }
//           .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
//           .header p { margin: 0; }
//           h1 { text-align: center; margin-bottom: 20px; }
//           table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
//           th, td { border: 1px solid #000; padding: 8px; text-align: left; }
//           th { background-color: #f2f2f2; }
//         </style>
//       </head>
//       <body>
//     `);

//     printWindow.document.write(`
//       <div class="header">
//         <div><p>COPIFOR DE DAROU NGARAF</p></div>
//         <div><p>Période du ${startDate} au ${endDate}</p></div>
//       </div>
//     `);

//     printWindow.document.write("<h1>Situation de Caisse</h1>");
//     printWindow.document.write("<table>");
//     printWindow.document.write(`
//       <tr>
//         <th>Total Caisse Fin du Mois</th>
//         <th>Total Sortie Caisse</th>
//         <th>Total Solde Caisse</th>
//         <th>Solde Effectif Caisse</th>
//         <th>Différence à Justifier</th>
//         <th>Total Trésorerie</th>
//       </tr>
//     `);

//     rows.forEach((row) => {
//       printWindow.document.write(`
//         <tr>
//           <td>${row.totalCaisseFm}</td>
//           <td>${row.totalSortieCaisseFm}</td>
//           <td>${row.totalSoldeCaisseFm}</td>
//           <td>${row.soldeEffCaisseFm}</td>
//           <td>${row.differenceAJustifier}</td>
//           <td>${row.totalTreso}</td>
//         </tr>
//       `);
//     });

//     printWindow.document.write("</table>");
//     printWindow.document.write("</body></html>");
//     printWindow.document.close();
//     printWindow.print();
//   };

//   const handleEditClick = (row) => {
//     setEditRow(row);
//     setEditValue(row.soldeEffCaisseFm);
//     setOpenDialog(true);
//   };

//   const handleDialogClose = () => {
//     setOpenDialog(false);
//     setEditRow(null);
//     setEditValue("");
//   };

//   const handleDialogSave = () => {
//     if (!editRow) return;
//     handleUpdate(editRow.id, editValue);
//     handleDialogClose();
//   };

//   const columnsSituationCaisse = [
//     {
//       field: "totalCaisseFm",
//       headerName: "Total Caisse Fin du Mois",
//       flex: 1,
//     },
//     {
//       field: "totalSortieCaisseFm",
//       headerName: "Total Sortie Caisse",
//       flex: 1,
//     },
//     {
//       field: "totalSoldeCaisseFm",
//       headerName: "Total Solde Caisse",
//       flex: 1,
//     },
//     {
//       field: "soldeEffCaisseFm",
//       headerName: "Solde Effectif Caisse",
//       flex: 1,
//     },
//     {
//       field: "differenceAJustifier",
//       headerName: "Différence à Justifier",
//       flex: 1,
//     },
//     {
//       field: "totalTreso",
//       headerName: "Total Trésorerie",
//       flex: 1,
//     },
//     {
//       field: "actions",
//       headerName: "Actions",
//       flex: 0.7,
//       sortable: false,
//       filterable: false,
//       renderCell: (params) => (
//         <IconButton onClick={() => handleEditClick(params.row)}>
//           <Edit />
//         </IconButton>
//       ),
//     },
//   ];

//   const CustomNoRowsOverlay = () => (
//     <GridOverlay>
//       <Typography>Aucune donnée disponible</Typography>
//     </GridOverlay>
//   );

//   return (
//     <Box p={2}>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={2}
//       >
//         <Box display="flex" gap={2}>
//           <TextField
//             id="start-date"
//             label="Période de début"
//             type="date"
//             value={convertToYYYYMMDD(startDate)}
//             onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
//             InputLabelProps={{ shrink: true }}
//           />
//           <TextField
//             id="end-date"
//             label="Période de fin"
//             type="date"
//             value={convertToYYYYMMDD(endDate)}
//             onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
//             InputLabelProps={{ shrink: true }}
//           />
//         </Box>

//         <Box>
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={handleSave}
//             startIcon={<DownloadOutlined />}
//             disabled={isFetchingSituationCaisse}
//           >
//             Enregistrer
//           </Button>
//           <Button
//             variant="contained"
//             color="secondary"
//             onClick={handlePrint}
//             startIcon={<DownloadOutlined />}
//             style={{ marginLeft: 16 }}
//             disabled={isFetchingSituationCaisse}
//           >
//             Imprimer
//           </Button>
//         </Box>
//       </Box>

//       <DataGrid
//         rows={rows}
//         columns={columnsSituationCaisse}
//         autoHeight
//         loading={isFetchingSituationCaisse}
//         pageSizeOptions={[10, 25, 50, 100]}
//         slots={{
//           noRowsOverlay: CustomNoRowsOverlay,
//         }}
//       />

//       <Dialog open={openDialog} onClose={handleDialogClose}>
//         <DialogTitle>Modifier Solde Effectif Caisse</DialogTitle>
//         <DialogContent>
//           <TextField
//             label="Solde Effectif Caisse"
//             type="number"
//             value={editValue}
//             onChange={(e) => setEditValue(e.target.value)}
//             fullWidth
//             margin="dense"
//           />
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleDialogClose}>Annuler</Button>
//           <Button onClick={handleDialogSave}>Enregistrer</Button>
//         </DialogActions>
//       </Dialog>
//     </Box>
//   );
// };

// export default SituationCaisse;

